import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const progressTrackingKeys = {
  all: ['projectProgressTracking'],
  byProject: (projectId) => [...progressTrackingKeys.all, 'project', projectId],
  byTemplate: (projectId, templateId) => [...progressTrackingKeys.all, 'template', projectId, templateId],
  specific: (projectId, taskId) => [...progressTrackingKeys.all, 'specific', projectId, taskId],
  summary: (projectId) => [...progressTrackingKeys.all, 'summary', projectId],
};

/**
 * Hook para obtener el progreso de todas las tareas de un proyecto
 * @param {string} projectId - UUID del proyecto
 * @param {Object} options - Opciones adicionales para la query
 */
export const useProjectProgressTracking = (projectId, options = {}) => {
  return useQuery({
    queryKey: progressTrackingKeys.byProject(projectId),
    queryFn: async () => {
      const response = await apiClient.get(`project_progress_tracking/by-project/${projectId}`);
      return response.data;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener el resumen de progreso de un proyecto
 * @param {string} projectId - UUID del proyecto
 * @param {Object} options - Opciones adicionales para la query
 */
export const useProjectProgressSummary = (projectId, options = {}) => {
  return useQuery({
    queryKey: progressTrackingKeys.summary(projectId),
    queryFn: async () => {
      const response = await apiClient.get(`project_progress_tracking/by-project/${projectId}/summary`);
      return response.data;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener el progreso de un template específico en un proyecto
 * @param {string} projectId - UUID del proyecto
 * @param {string} templateId - UUID del template
 * @param {Object} options - Opciones adicionales para la query
 */
export const useProgressByTemplate = (projectId, templateId, options = {}) => {
  return useQuery({
    queryKey: progressTrackingKeys.byTemplate(projectId, templateId),
    queryFn: async () => {
      const response = await apiClient.get(`project_progress_tracking/by-template/${projectId}/${templateId}`);
      return response.data;
    },
    enabled: !!(projectId && templateId),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener el progreso específico de una tarea en un proyecto
 * @param {string} projectId - UUID del proyecto
 * @param {string} taskId - UUID de la tarea
 * @param {Object} options - Opciones adicionales para la query
 */
export const useProgressTracking = (projectId, taskId, options = {}) => {
  return useQuery({
    queryKey: progressTrackingKeys.specific(projectId, taskId),
    queryFn: async () => {
      const response = await apiClient.get(`project_progress_tracking/${projectId}/${taskId}`);
      return response.data;
    },
    enabled: !!(projectId && taskId),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para actualizar el progreso de una tarea
 */
export const useUpdateProgressTracking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, taskId, data }) => {
      const response = await apiClient.put(`project_progress_tracking/${projectId}/${taskId}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        progressTrackingKeys.specific(variables.projectId, variables.taskId), 
        data
      );
      queryClient.invalidateQueries({ queryKey: progressTrackingKeys.byProject(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: progressTrackingKeys.summary(variables.projectId) });
    },
    onError: (error) => {
      console.error('Error updating progress tracking:', error);
    },
  });
};

/**
 * Hook para actualización masiva de progreso
 */
export const useBulkUpdateProgress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, progressUpdates }) => {
      const response = await apiClient.put(`project_progress_tracking/bulk-update/${projectId}`, {
        progress_updates: progressUpdates
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: progressTrackingKeys.all });
      queryClient.invalidateQueries({ queryKey: progressTrackingKeys.byProject(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: progressTrackingKeys.summary(variables.projectId) });
    },
    onError: (error) => {
      console.error('Error bulk updating progress:', error);
    },
  });
};
